import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'
import { match } from 'ts-pattern'
import { OutcomeTag } from '../../components/outcomes/outcome-tag.tsx'
import type { OutcomeType } from '../../components/outcomes/outcome-types.ts'
import { RenderedMarkdown } from '../../components/rendered-markdown.tsx'
import type { AntaresFinding, DetectionOutcome, TriageLocation } from '../../mock-data/antares-scans/index.ts'
import { computeRunStats, getAntaresRun } from '../../mock-data/antares-scans/index.ts'
import { Routes } from '../../routes.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { AntaresMark } from '../antares-scans-page/antares-mark.tsx'
import * as styles from './antares-scan-details-page.css.ts'

type Classification = 'confirmed' | 'uncertain' | 'hypothesis'
type QuickView = 'all' | Classification

const classifyFinding = (finding: AntaresFinding): Classification =>
  match(finding.triage?.result_type)
    .with('TRUE_POSITIVE', () => 'confirmed' as const)
    .with('UNCERTAIN', () => 'uncertain' as const)
    .otherwise(() => 'hypothesis' as const)

const classificationLabel: Record<Classification, string> = {
  confirmed: 'True positive',
  uncertain: 'Likely true positive',
  hypothesis: 'False positive',
}

const classificationOutcomeType: Record<Classification, OutcomeType> = {
  confirmed: 'true-positive',
  uncertain: 'likely-true-positive',
  hypothesis: 'no-fix-necessary-false-positive',
}

const likelihoodBucket = (finding: AntaresFinding): 'high' | 'medium' | 'low' | 'unknown' =>
  match(finding.likelihood_of_exploit.toLowerCase())
    .with('high', () => 'high' as const)
    .with('medium', () => 'medium' as const)
    .with('low', () => 'low' as const)
    .otherwise(() => 'unknown' as const)

const languageFromFilePath = (filePath: string): string => {
  const ext = filePath.split('.').pop()?.toLowerCase() ?? ''
  return match(ext)
    .with('java', () => 'java')
    .with('ts', 'tsx', () => 'typescript')
    .with('js', 'jsx', 'mjs', 'cjs', () => 'javascript')
    .with('py', () => 'python')
    .with('go', () => 'go')
    .with('rb', () => 'ruby')
    .with('rs', () => 'rust')
    .with('kt', () => 'kotlin')
    .with('cs', () => 'csharp')
    .with('php', () => 'php')
    .with('c', 'h', () => 'c')
    .with('cpp', 'cc', 'cxx', 'hpp', () => 'cpp')
    .otherwise(() => 'text')
}

export const AntaresScanDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const entry = slug ? getAntaresRun(slug) : undefined
  if (!entry) {
    return <Navigate to={Routes.AntaresScansPage.path} replace />
  }

  const { report } = entry
  const stats = computeRunStats(report)

  return (
    <div className={styles.pageContainer}>
      <ScanBreadcrumb repoLabel={entry.label} />
      <ScanHeader
        entry={entry}
        confirmedCount={stats.findings.length}
        uncertainCount={stats.uncertain.length}
        hypothesisCount={stats.investigations.length}
        cweCount={stats.cwes}
      />
      <FindingsSection
        findings={report.findings}
        counts={{
          confirmed: stats.findings.length,
          uncertain: stats.uncertain.length,
          hypothesis: stats.investigations.length,
        }}
      />
      <CweCoverageSection
        detection={report.phases.detection}
        planByStep={new Map(report.phases.cwe_plan.entries.map(e => [e.step_uuid, e.vuln_class]))}
      />
      <AttributionFooter />
    </div>
  )
}

type ScanBreadcrumbProps = {
  repoLabel: string
}

const ScanBreadcrumb: React.FC<ScanBreadcrumbProps> = ({ repoLabel }) => (
  <div className={styles.breadcrumbRow}>
    <Link to={Routes.AntaresScansPage.path} className={styles.breadcrumbLink}>
      All scans
    </Link>
    <span className={styles.breadcrumbSeparator}>·</span>
    <span className={styles.breadcrumbCurrent}>{repoLabel}</span>
  </div>
)

type ScanHeaderProps = {
  entry: NonNullable<ReturnType<typeof getAntaresRun>>
  confirmedCount: number
  uncertainCount: number
  hypothesisCount: number
  cweCount: number
}

const ScanHeader: React.FC<ScanHeaderProps> = ({
  entry,
  confirmedCount,
  uncertainCount,
  hypothesisCount,
  cweCount,
}) => {
  const { report } = entry
  const gitHead = report.git_head ? report.git_head.slice(0, 12) : undefined
  const totalPositions = confirmedCount + uncertainCount + hypothesisCount

  return (
    <header className={styles.headerGrid}>
      <div className={styles.cellR1C1}>
        <AntaresMark size={40} />
        <div>
          <div className={styles.detectorLabel}>Antares × Pixee</div>
          <div className={styles.detectorSubLabel}>Cisco Foundation AI · MITRE Top 25</div>
        </div>
      </div>

      <div className={styles.cellR2C1}>
        <div className={styles.dateTimeContainer}>
          <p className={styles.dateText}>{formatDate({ date: report.started_at, type: 'mm/dd/yy' })}</p>
          <p className={styles.timeText}>{formatDate({ date: report.started_at, type: 'time-with-zone' })}</p>
        </div>
        <div className={styles.scanMetaRow}>
          {gitHead && <span className={styles.branchTag}>{gitHead}</span>}
          <span className={styles.targetPath}>{report.target}</span>
        </div>
      </div>

      <div className={styles.cellR1C2}>
        <div className={styles.repoTitleRow}>
          <h1 className={styles.repoTitle}>{entry.label}</h1>
          <span className={styles.languageChip}>{entry.language}</span>
          <span className={styles.kindTag}>{entry.kind}</span>
        </div>
      </div>

      <div className={styles.cellR2C2}>
        <div className={styles.compositionBarStack}>
          <div className={styles.compositionBarTrack} aria-label="Findings composition">
            <CompositionSegment kind="confirmed" count={confirmedCount} total={totalPositions} />
            <CompositionSegment kind="uncertain" count={uncertainCount} total={totalPositions} />
            <CompositionSegment kind="hypothesis" count={hypothesisCount} total={totalPositions} />
          </div>
          <div className={styles.compositionLegend}>
            <LegendItem kind="confirmed" count={confirmedCount} />
            <LegendItem kind="uncertain" count={uncertainCount} />
            <LegendItem kind="hypothesis" count={hypothesisCount} />
          </div>
        </div>
      </div>

      <div className={styles.cellR1C3}>
        <span className={styles.attributionBadge}>
          <AntaresMark size={14} /> Antares × Pixee
        </span>
      </div>

      <div className={styles.cellR2C3}>
        <div className={styles.metricsRow}>
          <div className={styles.metric}>
            <span className={styles.metricValuePrimary}>{confirmedCount + uncertainCount}</span>
            <span className={styles.metricLabel}>Findings</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricValue}>{hypothesisCount}</span>
            <span className={styles.metricLabel}>False positives</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricValue}>{cweCount}</span>
            <span className={styles.metricLabel}>CWEs</span>
          </div>
        </div>
      </div>
    </header>
  )
}

type CompositionSegmentProps = {
  kind: Classification
  count: number
  total: number
}

const CompositionSegment: React.FC<CompositionSegmentProps> = ({ kind, count, total }) => {
  if (count === 0 || total === 0) {
    return null
  }
  const pct = (count / total) * 100
  return <span className={styles.compositionSegment[kind]} style={{ width: `${pct}%` }} aria-hidden="true" />
}

type LegendItemProps = {
  kind: Classification
  count: number
}

const LegendItem: React.FC<LegendItemProps> = ({ kind, count }) => (
  <span className={styles.compositionLegendItem}>
    <span className={styles.compositionSwatch[kind]} />
    {count} {classificationLabel[kind]}
  </span>
)

type FindingsSectionProps = {
  findings: AntaresFinding[]
  counts: Record<Classification, number>
}

const FindingsSection: React.FC<FindingsSectionProps> = ({ findings, counts }) => {
  const [quickView, setQuickView] = useState<QuickView>('all')
  const visibleFindings = useMemo(
    () => (quickView === 'all' ? findings : findings.filter(f => classifyFinding(f) === quickView)),
    [findings, quickView]
  )

  return (
    <section>
      <div className={styles.tabsRow} role="tablist">
        <QuickViewTab
          quickView={quickView}
          value="all"
          count={findings.length}
          label="All findings"
          onSelect={setQuickView}
        />
        <QuickViewTab
          quickView={quickView}
          value="confirmed"
          count={counts.confirmed}
          label="True positive"
          onSelect={setQuickView}
        />
        <QuickViewTab
          quickView={quickView}
          value="uncertain"
          count={counts.uncertain}
          label="Likely true positive"
          onSelect={setQuickView}
        />
        <QuickViewTab
          quickView={quickView}
          value="hypothesis"
          count={counts.hypothesis}
          label="False positive"
          onSelect={setQuickView}
        />
      </div>
      <FindingsTable findings={visibleFindings} />
    </section>
  )
}

type QuickViewTabProps = {
  quickView: QuickView
  value: QuickView
  count: number
  label: string
  onSelect: (value: QuickView) => unknown
}

const QuickViewTab: React.FC<QuickViewTabProps> = ({ quickView, value, count, label, onSelect }) => {
  const isActive = quickView === value
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={isActive ? styles.tabActive : styles.tab}
      onClick={() => onSelect(value)}
    >
      {label}
      <span className={styles.tabCount}>{count}</span>
    </button>
  )
}

type FindingsTableProps = {
  findings: AntaresFinding[]
}

const FindingsTable: React.FC<FindingsTableProps> = ({ findings }) => {
  if (findings.length === 0) {
    return (
      <div className={styles.findingsTable}>
        <div className={styles.emptyState}>No findings match this view.</div>
      </div>
    )
  }

  return (
    <div className={styles.findingsTable}>
      <div className={styles.tableHeaderRow} role="row">
        <span>Likelihood</span>
        <span>Finding</span>
        <span>CWE</span>
        <span>Classification</span>
        <span />
      </div>
      {findings.map(finding => (
        <FindingRow key={finding.finding_uuid} finding={finding} />
      ))}
    </div>
  )
}

type FindingRowProps = {
  finding: AntaresFinding
}

const FindingRow: React.FC<FindingRowProps> = ({ finding }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const classification = classifyFinding(finding)
  const likelihood = likelihoodBucket(finding)
  const likelihoodLabel = finding.likelihood_of_exploit || 'Unknown'

  const handleToggle = () => setIsExpanded(prev => !prev)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleToggle()
    }
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={styles.tableRow}
        aria-expanded={isExpanded}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span className={styles.likelihoodChip[likelihood]}>{likelihoodLabel}</span>
        <div className={styles.findingTitleCell}>
          <span className={styles.findingTitle}>{finding.title}</span>
          <span className={styles.findingFile}>{finding.file_path}</span>
        </div>
        <span className={styles.cweCellText}>{finding.cwe_ids.join(', ')}</span>
        <OutcomeTag outcomeType={classificationOutcomeType[classification]} />
        <span className={styles.chevronCell}>
          {isExpanded ? <CaretDownIcon size={16} weight="bold" /> : <CaretRightIcon size={16} weight="bold" />}
        </span>
      </div>
      {isExpanded && (
        <div className={styles.expandedPanel}>
          {finding.triage?.tldr && (
            <div>
              <div className={styles.expandedLabel}>Summary</div>
              <div className={styles.expandedMarkdown}>
                <RenderedMarkdown markdown={finding.triage.tldr} />
              </div>
            </div>
          )}
          {finding.triage?.full_report && (
            <div>
              <div className={styles.expandedLabel}>Triage reasoning</div>
              <div className={styles.expandedMarkdown}>
                <RenderedMarkdown markdown={finding.triage.full_report} />
              </div>
            </div>
          )}
          {finding.triage?.location && (
            <LocationSnippet filePath={finding.file_path} location={finding.triage.location} />
          )}
        </div>
      )}
    </>
  )
}

type LocationSnippetProps = {
  filePath: string
  location: TriageLocation
}

const LocationSnippet: React.FC<LocationSnippetProps> = ({ filePath, location }) => {
  const language = languageFromFilePath(filePath)
  const range =
    location.line_start === location.line_end
      ? `L${location.line_start}`
      : `L${location.line_start}-${location.line_end}`
  const fenced = `\`\`\`${language}\n${location.snippet}\n\`\`\``

  return (
    <div>
      <div className={styles.expandedLabel}>
        Code inspected ·{' '}
        <span className={styles.locationCoords}>
          {filePath}:{range}
        </span>
      </div>
      <div className={styles.expandedMarkdown}>
        <RenderedMarkdown markdown={fenced} />
      </div>
    </div>
  )
}

type CweCoverageSectionProps = {
  detection: DetectionOutcome[]
  planByStep: Map<string, string>
}

const CweCoverageSection: React.FC<CweCoverageSectionProps> = ({ detection, planByStep }) => (
  <section>
    <div className={styles.secondarySectionHeader}>
      <h2 className={styles.secondarySectionTitle}>CWE coverage</h2>
      <span className={styles.secondarySectionCount}>{detection.length} examined</span>
    </div>
    <p className={styles.secondarySectionLede}>
      Every CWE category the pipeline attempted, whether it converged, and how much of the tool-call budget it used.
    </p>
    <div style={{ marginTop: '12px' }}>
      <div className={styles.cweCoverageTable}>
        <div className={styles.cweCoverageHead} role="row">
          <span>CWE / Vulnerability class</span>
          <span>Findings</span>
          <span>Tool calls</span>
          <span>Status</span>
        </div>
        {detection.map(d => {
          const status = match({ incomplete: Boolean(d.incomplete_reason), findings: d.findings.length })
            .with({ incomplete: true }, () => ({ label: 'Incomplete', cls: styles.cweCoverageStatus.incomplete }))
            .with({ findings: 0 }, () => ({ label: 'Cleared', cls: styles.cweCoverageStatus.cleared }))
            .otherwise(() => ({ label: 'Submitted', cls: styles.cweCoverageStatus.found }))

          return (
            <div key={d.step_uuid} className={styles.cweCoverageRow} role="row">
              <div className={styles.cweCoverageClassCell}>
                <span className={styles.cweCoverageClassCode}>{d.cwe_ids.join(', ')}</span>
                <span className={styles.cweCoverageClassName}>{planByStep.get(d.step_uuid) ?? ''}</span>
              </div>
              <div className={styles.cweCoverageNumericCell}>{d.findings.length}</div>
              <div className={styles.cweCoverageNumericCell}>{d.tool_call_count}</div>
              <div className={status.cls}>{status.label}</div>
            </div>
          )
        })}
      </div>
    </div>
  </section>
)

const AttributionFooter: React.FC = () => (
  <div className={styles.attributionFooter}>
    <AntaresMark size={22} />
    <p className={styles.attributionText}>
      Detection powered by <span className={styles.attributionStrong}>Antares</span>, a security-specialized language
      model from Cisco Foundation AI. Threat modeling, CWE planning, and finding validation use Pixee's reasoning
      models.
    </p>
  </div>
)
